import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  Search,
  Label,
  ExpandMore,
  LocalOffer,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TagService, Tag } from '../api/services/tagService';

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#64748b', '#78716c',
];

const CATEGORY_OPTIONS = [
  { value: 'genre', label: 'Genre' },
  { value: 'mood', label: 'Mood' },
  { value: 'era', label: 'Era/Decade' },
  { value: 'energy', label: 'Energy Level' },
  { value: 'tempo', label: 'Tempo' },
  { value: 'vocal', label: 'Vocal Style' },
  { value: 'instrument', label: 'Instrument' },
  { value: 'occasion', label: 'Occasion' },
  { value: 'custom', label: 'Custom' },
];

// Hooks for tags
function useTags(category?: string) {
  return useQuery({
    queryKey: ['tags', category],
    queryFn: () => TagService.getTags(category),
  });
}

function useCreateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; category: string; color?: string; description?: string }) =>
      TagService.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

function useUpdateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; color?: string; description?: string; sort_order?: number; is_active?: boolean } }) =>
      TagService.updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

function useDeleteTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => TagService.deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

const TagsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const { data: tagsData, isLoading, error } = useTags();
  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag();
  const deleteMutation = useDeleteTag();

  const [formData, setFormData] = useState({
    name: '',
    category: 'genre',
    color: '#3b82f6',
    description: '',
  });

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      category: 'genre',
      color: '#3b82f6',
      description: '',
    });
    setEditingTag(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (tag: Tag) => {
    setFormData({
      name: tag.name,
      category: tag.category,
      color: tag.color,
      description: tag.description || '',
    });
    setEditingTag(tag);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingTag) {
        await updateMutation.mutateAsync({
          id: editingTag.id,
          data: formData,
        });
        setSnackbar({ open: true, message: 'Tag updated' });
      } else {
        await createMutation.mutateAsync(formData);
        setSnackbar({ open: true, message: 'Tag created' });
      }
      setCreateDialogOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save tag' });
    }
  };

  const handleDelete = async (tag: Tag) => {
    if (window.confirm(`Delete tag "${tag.name}"? This will remove it from all content.`)) {
      try {
        await deleteMutation.mutateAsync(tag.id);
        setSnackbar({ open: true, message: 'Tag deleted' });
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to delete tag' });
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load tags: {(error as Error)?.message}
      </Alert>
    );
  }

  // Filter tags based on search and category
  const filteredTags = tagsData?.tags?.filter((tag) => {
    const matchesSearch = !searchQuery ||
      tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tag.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || tag.category === filterCategory;
    return matchesSearch && matchesCategory;
  }) || [];

  // Group filtered tags by category
  const groupedTags = filteredTags.reduce((acc, tag) => {
    if (!acc[tag.category]) acc[tag.category] = [];
    acc[tag.category].push(tag);
    return acc;
  }, {} as Record<string, Tag[]>);

  const totalTags = tagsData?.tags?.length || 0;
  const categories = tagsData?.categories || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Tags
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage content tags for organization and filtering
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
        >
          New Tag
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                {totalTags}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Tags
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#10b981' }}>
                {categories.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1 }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={filterCategory}
              label="Category"
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Tags by Category */}
      {Object.entries(groupedTags).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LocalOffer sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No tags found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchQuery || filterCategory ? 'Try adjusting your filters' : 'Create your first tag to get started'}
          </Typography>
          {!searchQuery && !filterCategory && (
            <Button variant="contained" onClick={handleOpenCreate}>
              Create Tag
            </Button>
          )}
        </Paper>
      ) : (
        Object.entries(groupedTags)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, tags]) => (
            <Accordion key={category} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Label sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </Typography>
                  <Chip
                    size="small"
                    label={tags.length}
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tags.sort((a, b) => a.name.localeCompare(b.name)).map((tag) => (
                    <Tooltip
                      key={tag.id}
                      title={
                        <Box>
                          {tag.description && (
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                              {tag.description}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Click to edit
                          </Typography>
                        </Box>
                      }
                    >
                      <Chip
                        label={tag.name}
                        onClick={() => handleOpenEdit(tag)}
                        onDelete={() => handleDelete(tag)}
                        sx={{
                          bgcolor: `${tag.color}20`,
                          color: tag.color,
                          borderColor: tag.color,
                          border: '1px solid',
                          fontWeight: 500,
                          '&:hover': {
                            bgcolor: `${tag.color}30`,
                          },
                          '& .MuiChip-deleteIcon': {
                            color: tag.color,
                            '&:hover': {
                              color: '#ef4444',
                            },
                          },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTag ? 'Edit Tag' : 'Create New Tag'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Jazz, Upbeat, 1980s"
            />

            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {CATEGORY_OPTIONS.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this tag"
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: color,
                      cursor: 'pointer',
                      border: formData.color === color ? '3px solid white' : 'none',
                      boxShadow: formData.color === color ? `0 0 0 2px ${color}` : 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Preview */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Preview</Typography>
              <Chip
                label={formData.name || 'Tag Name'}
                sx={{
                  bgcolor: `${formData.color}20`,
                  color: formData.color,
                  borderColor: formData.color,
                  border: '1px solid',
                  fontWeight: 500,
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
          >
            {editingTag ? 'Save Changes' : 'Create Tag'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default TagsPage;
